import { type V3ActivityStatus } from '@apps/web/features/phoebe_chat_v3/model/activity_presentation';
import { displayReasoning } from '@apps/web/features/phoebe_chat_v3/model/reasoning_trace';
import {
    type V3ChatEntry,
    type V3MessageEntry,
    type V3ReasoningMilestoneStep,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

const INTENT_TITLE_LIMIT = 60;

function truncateIntent(value: string): string {
    if (value.length <= INTENT_TITLE_LIMIT) {
        return value;
    }
    return `${value.slice(0, INTENT_TITLE_LIMIT - 1).trimEnd()}…`;
}

export function workingIntentTitle(value: string): string | null {
    const heading = /^\s*\*\*(.+?)\*\*/.exec(value);
    const headingText = heading?.[1]?.replace(/\s+/g, ' ').trim();
    if (headingText) {
        return truncateIntent(headingText);
    }
    const displayed = displayReasoning(value);
    if (displayed === null) {
        return null;
    }
    const firstLine = displayed.split('\n')[0] ?? '';
    const firstLineIsSettled =
        value.includes('\n') ||
        /[.!?]\s/.test(displayed) ||
        /[.!?]$/.test(firstLine) ||
        firstLine.length >= 24;
    if (!firstLineIsSettled) {
        return null;
    }
    return truncateIntent(firstLine);
}

function lastUserEntryIndex(entries: ReadonlyArray<V3ChatEntry>): number {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry?.kind === 'message' && entry.role === 'user') {
            return index;
        }
    }
    return -1;
}

function entriesAfterLastUser(
    entries: ReadonlyArray<V3ChatEntry>,
): ReadonlyArray<V3ChatEntry> {
    return entries.slice(lastUserEntryIndex(entries) + 1);
}

export function latestTurnWorkingState(entries: ReadonlyArray<V3ChatEntry>): {
    readonly key: string;
    readonly milestones: ReadonlyArray<V3ReasoningMilestoneStep>;
} {
    const userIndex = lastUserEntryIndex(entries);
    const userEntry = entries[userIndex];
    const milestones: Array<V3ReasoningMilestoneStep> = [];
    for (let index = userIndex + 1; index < entries.length; index += 1) {
        const entry = entries[index];
        if (entry?.kind !== 'reasoning') {
            continue;
        }
        for (const step of entry.steps) {
            if (step.type === 'milestone') {
                milestones.push(step);
            }
        }
    }
    return {
        key:
            userEntry?.kind === 'message' && userEntry.role === 'user'
                ? userEntry.id
                : 'before-first-user-message',
        milestones,
    };
}

export function hasVisibleAssistantAnswer({
    durableEntries,
    liveEntries,
}: {
    readonly durableEntries: ReadonlyArray<V3ChatEntry>;
    readonly liveEntries: ReadonlyArray<V3MessageEntry>;
}): boolean {
    if (liveEntries.some((entry) => entry.content.length > 0)) {
        return true;
    }
    return entriesAfterLastUser(durableEntries).some(
        (entry) =>
            entry.kind === 'message' &&
            entry.role === 'assistant' &&
            entry.content.length > 0,
    );
}

// The placeholder shown when the run reports nothing else. It is display
// filler, not a real step: the working trace must never record it as a
// completed task, and should keep showing the last real status instead of
// flickering back to it between tool calls.
const FALLBACK_WORKING_STATUS: V3ActivityStatus = {
    kind: 'decision',
    label: 'Working…',
    detail: null,
};

export function isFallbackV3WorkingStatus(status: V3ActivityStatus): boolean {
    return (
        status.kind === FALLBACK_WORKING_STATUS.kind &&
        status.label === FALLBACK_WORKING_STATUS.label &&
        status.detail === null
    );
}

export function isDisplayOnlyV3WorkingStatus(
    status: V3ActivityStatus,
): boolean {
    return status.kind === 'replying' || isFallbackV3WorkingStatus(status);
}

export function resolveV3WorkingStatus({
    hasVisibleAnswer,
    activeStatus,
    liveReasoningTitle,
    lastMilestoneLabel: lastStepLabel = null,
}: {
    readonly hasVisibleAnswer: boolean;
    readonly activeStatus: V3ActivityStatus | null;
    readonly liveReasoningTitle: string | null;
    readonly lastMilestoneLabel?: string | null;
}): V3ActivityStatus {
    if (activeStatus !== null) {
        return activeStatus;
    }
    if (hasVisibleAnswer) {
        return {
            kind: 'replying',
            label: 'Writing response',
            detail: null,
        };
    }
    if (liveReasoningTitle !== null) {
        return {
            kind: 'decision',
            label: liveReasoningTitle,
            detail: null,
        };
    }
    if (lastStepLabel !== null) {
        return {
            kind: 'general',
            label: lastStepLabel,
            detail: null,
        };
    }
    return FALLBACK_WORKING_STATUS;
}
